import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { MdIron, MdLocalLaundryService, MdDryCleaning } from 'react-icons/md';
import { GiRunningShoe } from 'react-icons/gi';
import HomeCarousel from '../components/HomeCarousel';
import PricingBanner from '../components/PricingBanner';
import { fadeInUp, scaleIn, staggerContainer } from '../hooks/useScrollAnimation';

const PrimaryButton = styled(Link)`
  background-color: var(--primary-color);
  color: white;
  padding: 12px 25px;
  border-radius: var(--border-radius);
  font-weight: 500;
  display: inline-block;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--secondary-color);
    color: white;
    transform: translateY(-3px);
  }
`;

const ServicesSection = styled.section`
  padding: 80px 0;
  background: var(--background-accent);
  position: relative;
  border-top: 5px solid var(--primary-color);
  border-bottom: 5px solid var(--primary-color);
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
  
  /* Center last card when it's alone */
  @media (min-width: 969px) {
    & > *:last-child:nth-child(4) {
      grid-column: 2 / 3;
    }
  }
  
  @media (max-width: 968px) and (min-width: 601px) {
    & > *:last-child:nth-child(odd) {
      grid-column: 1 / -1;
      max-width: 400px;
      margin: 0 auto;
    }
  }
`;

const ServiceCard = styled(motion.div)`
  background: linear-gradient(135deg, var(--background-lighter) 0%, rgba(245, 241, 232, 0.3) 100%);
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid var(--primary-color);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.2), transparent);
    transition: left 0.6s ease-out;
  }
  
  &:hover {
    transform: translateY(-12px) scale(1.03) rotate(-1deg);
    box-shadow: 0 15px 40px rgba(212, 175, 55, 0.25);
    border-color: var(--secondary-color);
    background: linear-gradient(135deg, var(--background-lighter) 0%, rgba(212, 175, 55, 0.15) 100%);
    
    &::before {
      left: 100%;
    }
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--accent-color) 0%, rgba(212, 175, 55, 0.1) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  transition: all 0.4s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px solid var(--primary-color);
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.4s ease;
  }
  
  ${ServiceCard}:hover & {
    transform: rotate(360deg) scale(1.1);
    
    &::after {
      opacity: 1;
      transform: scale(1.2);
    }
  }
  
  svg {
    font-size: 2.2rem;
    color: var(--primary-color);
    transition: all 0.3s ease;
  }
  
  ${ServiceCard}:hover & svg {
    color: var(--secondary-color);
    transform: scale(1.1);
  }
`;


const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 15px;
`;

const ServiceDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
`;

const FeaturesSection = styled.section`
  padding: 80px 0;
  background: var(--background-darker);
  position: relative;
  border-top: 5px solid var(--primary-color);
  border-bottom: 5px solid var(--primary-color);
`;

const FeatureContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 60px;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  
  @media (max-width: 768px) {
    flex-direction: ${props => props.$isReverse ? 'column-reverse' : 'column'};
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureImageContainer = styled.div`
  flex: 1;
  position: relative;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const FeatureImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: var(--border-radius);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const FeatureTitle = styled(motion.h2)`
  font-size: 2.2rem;
  color: var(--text-primary);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const FeatureDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 25px;
`;

const FeatureList = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin-bottom: 25px;
`;

const FeatureItem = styled(motion.li)`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  color: var(--text-secondary);
  
  &::before {
    content: '✓';
    display: inline-block;
    color: var(--primary-color);
    margin-right: 10px;
    font-weight: bold;
  }
`;

const CTASection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #D4AF37 0%, #C9A961 50%, #D4AF37 100%);
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50%;
    left: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const CTAContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CTATitle = styled(motion.h2)`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTADescription = styled(motion.p)`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  background-color: white;
  color: var(--primary-color);
  padding: 18px 45px;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.1rem;
  display: inline-block;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.6s ease-out;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    background: linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%);
    color: white;
    transform: translateY(-5px) scale(1.08) rotate(-1deg);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  }
  
  &:active {
    transform: translateY(-2px) scale(1.02);
  }
`;

const Home = () => {
  return (
    <>
      <HomeCarousel />

      <ServicesSection>
        <div className="section-title">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Our Services
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Specializing in premium steam ironing services, with additional dry cleaning, laundry, and shoes cleaning solutions
          </motion.p>
        </div>
        
        <ServiceGrid>
          <ServiceCard
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
            whileHover={{ scale: 1.05 }}
          >
            <ServiceIcon>
              <MdIron />
            </ServiceIcon>
            <ServiceTitle>Steam Ironing</ServiceTitle>
            <div style={{ display: 'inline-block', background: 'var(--primary-color)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>PRIMARY SERVICE</div>
            <ServiceDescription>
              Our specialty! Professional steam ironing service that removes wrinkles effectively and gives your clothes a crisp, fresh look with perfect finishing. Expert care for all fabric types.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15, type: "spring", stiffness: 100 }}
            whileHover={{ scale: 1.05 }}
          >
            <ServiceIcon>
              <MdDryCleaning />
            </ServiceIcon>
            <ServiceTitle>Dry Cleaning</ServiceTitle>
            <ServiceDescription>
              Expert dry cleaning services for delicate fabrics, suits, and special garments that require professional care and attention.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3, type: "spring", stiffness: 100 }}
            whileHover={{ scale: 1.05 }}
          >
            <ServiceIcon>
              <MdLocalLaundryService />
            </ServiceIcon>
            <ServiceTitle>Laundry</ServiceTitle>
            <ServiceDescription>
              Complete wash and fold laundry services with premium detergents, ensuring your clothes are clean, fresh, and perfectly folded.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.45, type: "spring", stiffness: 100 }}
            whileHover={{ scale: 1.05 }}
          >
            <ServiceIcon>
              <GiRunningShoe />
            </ServiceIcon>
            <ServiceTitle>Shoes Cleaning</ServiceTitle>
            <ServiceDescription>
              Professional shoe cleaning and care services for all types of footwear, restoring them to their original shine and freshness.
            </ServiceDescription>
          </ServiceCard>
        </ServiceGrid>
        
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <PricingBanner
            title="Steam Ironing at Unbeatable Prices!"
            description="Get up to 70% OFF on daily wear. Shirt/T-shirt & Trouser just ₹15 each!"
            discount="70% OFF"
            buttonText="View All Prices"
            buttonLink="/pricing"
          />
        </div>
      </ServicesSection>

      <FeaturesSection>
        <div className="section-title">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Premium <span>In-Shop</span> Facilities
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Experience exceptional amenities when you visit our service center
          </motion.p>
        </div>
        
        <ServiceGrid style={{ marginBottom: '60px', position: 'relative', zIndex: 2 }}>
          <ServiceCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ y: -10, transition: { duration: 0.3 } }}
            style={{ background: 'linear-gradient(135deg, var(--background-lighter) 0%, rgba(219, 226, 220, 0.3) 100%)' }}
          >
            <ServiceIcon style={{ background: 'linear-gradient(135deg, var(--accent-color) 0%, rgba(212, 175, 55, 0.2) 100%)' }}>
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                ☕
              </motion.div>
            </ServiceIcon>
            <ServiceTitle style={{ fontSize: '1.3rem' }}>Comfortable Waiting Lounge</ServiceTitle>
            <ServiceDescription>
              Relax in our air-conditioned waiting lounge with complimentary refreshments while we handle your garments.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            whileHover={{ y: -10, transition: { duration: 0.3 } }}
            style={{ background: 'linear-gradient(135deg, var(--background-lighter) 0%, rgba(219, 226, 220, 0.3) 100%)' }}
          >
            <ServiceIcon style={{ background: 'linear-gradient(135deg, var(--accent-color) 0%, rgba(212, 175, 55, 0.2) 100%)' }}>
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                🚪
              </motion.div>
            </ServiceIcon>
            <ServiceTitle style={{ fontSize: '1.3rem' }}>Private Changing Rooms</ServiceTitle>
            <ServiceDescription>
              Clean, private changing rooms available for your convenience. Try on freshly pressed clothes before leaving.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            whileHover={{ y: -10, transition: { duration: 0.3 } }}
            style={{ background: 'linear-gradient(135deg, var(--background-lighter) 0%, rgba(219, 226, 220, 0.3) 100%)' }}
          >
            <ServiceIcon style={{ background: 'linear-gradient(135deg, var(--accent-color) 0%, rgba(212, 175, 55, 0.2) 100%)' }}>
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              >
                ✨
              </motion.div>
            </ServiceIcon>
            <ServiceTitle style={{ fontSize: '1.3rem' }}>Hygienic Facilities</ServiceTitle>
            <ServiceDescription>
              Spotlessly clean washrooms and facilities maintained to the highest hygiene standards for your comfort.
            </ServiceDescription>
          </ServiceCard>
          
          <ServiceCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.45 }}
            whileHover={{ y: -10, transition: { duration: 0.3 } }}
            style={{ background: 'linear-gradient(135deg, var(--background-lighter) 0%, rgba(219, 226, 220, 0.3) 100%)' }}
          >
            <ServiceIcon style={{ background: 'linear-gradient(135deg, var(--accent-color) 0%, rgba(212, 175, 55, 0.2) 100%)' }}>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
              >
                ⚡
              </motion.div>
            </ServiceIcon>
            <ServiceTitle style={{ fontSize: '1.3rem' }}>Express Service</ServiceTitle>
            <ServiceDescription>
              Need it fast? Our express service ensures your garments are ready within hours, not days.
            </ServiceDescription>
          </ServiceCard>
        </ServiceGrid>
        
        <FeatureContainer>
          <FeatureRow $isReverse={true}>
            <FeatureImageContainer>
              <FeatureImage 
                src="https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80" 
                alt="Free Delivery Service"
              />
            </FeatureImageContainer>
            
            <FeatureContent>
              <FeatureTitle
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Free <span>Pickup & Delivery</span> Service
              </FeatureTitle>
              <FeatureDescription
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                We understand your busy schedule, which is why we offer complimentary pickup and delivery services within a 5km radius of our location.
              </FeatureDescription>
              
              <FeatureList
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <FeatureItem variants={fadeInUp}>Free service within 5km radius</FeatureItem>
                <FeatureItem variants={fadeInUp}>Scheduled pickup and delivery times</FeatureItem>
                <FeatureItem variants={fadeInUp}>Careful handling and packaging</FeatureItem>
                <FeatureItem variants={fadeInUp}>Real-time tracking available</FeatureItem>
              </FeatureList>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <PrimaryButton to="/select-items">Place Order</PrimaryButton>
              </motion.div>
            </FeatureContent>
          </FeatureRow>
        </FeatureContainer>
      </FeaturesSection>

      <CTASection>
        <CTAContainer>
          <CTATitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Experience Premium Steam Ironing & Laundry Services Today
          </CTATitle>
          <CTADescription
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Join our satisfied customers and enjoy professional laundry, dry cleaning, ironing, and shoe cleaning services with free pickup and delivery.
          </CTADescription>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <CTAButton to="/select-items">Place Order Now</CTAButton>
          </motion.div>
        </CTAContainer>
      </CTASection>
    </>
  );
};

export default Home;
